import React, { useState, useCallback, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  Database, MapPin, ExternalLink, AlertCircle, BarChart3, Layers, Search as SearchIcon, Loader2,
  GitCompare, Check, X,
} from "lucide-react";
import { searchAtlas } from "../lib/api";
import { useMarket } from "../context/MarketContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PriceTag } from "../components/PriceTag";
import { AvailabilityBadge } from "../components/AvailabilityBadge";
import { Switch } from "../components/ui/switch";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Info } from "lucide-react";
import { km } from "../lib/format";
import { catalogMakes, modelsForMake, generationsFor, variantsFor } from "../lib/vehicleCatalog";

const SOURCES = [
  { value: "all", label: "All sources (Europe)" },
  { value: "autoscout24", label: "AutoScout24 · deep data" },
  { value: "mobile-de", label: "Mobile.de · deep data" },
  { value: "otomoto", label: "Otomoto · PL" },
  { value: "marktplaats", label: "Marktplaats · NL" },
  { value: "leboncoin", label: "Leboncoin · FR" },
];

// Known marketplace display names; unknown slugs are title-cased as a fallback.
const SITE_LABEL = {
  autoscout24: "AutoScout24", mobile_de: "Mobile.de", "mobile-de": "Mobile.de",
  otomoto: "Otomoto", marktplaats: "Marktplaats", leboncoin: "Leboncoin",
  lacentrale: "LaCentrale", olx: "OLX", finn: "Finn", autokopen: "AutoKopen",
  kleinanzeigen: "Kleinanzeigen", subito: "Subito", coches: "Coches.net",
  willhaben: "willhaben", instamotion: "Instamotion", autotrader: "AutoTrader UK",
};
const siteLabelOf = (v) => {
  const s = v.sourceData || {};
  if (s.sourceSite) {
    const slug = s.sourceSite.split(".")[0];
    return SITE_LABEL[slug] || (slug.charAt(0).toUpperCase() + slug.slice(1));
  }
  return SITE_LABEL[s.sourceCode] || s.sourceCode;
};

const basisBadge = (basis) =>
  basis === "region"
    ? { label: "confirmed from region", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" }
    : basis === "provider_country"
    ? { label: "confirmed country", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" }
    : basis === "source_default"
    ? { label: "source default", cls: "bg-amber-50 text-amber-700 border-amber-200" }
    : { label: "unknown", cls: "bg-slate-100 text-slate-600 border-slate-200" };

const compBadge = (cd) =>
  cd?.status === "OK"
    ? { label: `${cd.sampleSize} comparables`, cls: "bg-blue-50 text-blue-700 border-blue-200" }
    : { label: "insufficient comparables", cls: "bg-slate-100 text-slate-600 border-slate-200" };

const SourcePhoto = ({ src, alt, className, preview }) => {
  const [ok, setOk] = useState(!!src);
  useEffect(() => { setOk(!!src); }, [src]);  // reset when the listing/photo changes
  if (!ok) return <div className={`flex items-center justify-center text-muted-foreground text-sm ${className}`}>Image unavailable</div>;
  return (
    <>
      <img src={src} alt={alt} className={className} onError={() => setOk(false)} />
      {preview && (
        <span className="absolute bottom-2 left-2 text-[10px] px-1.5 py-0.5 rounded-sm bg-white/85 text-slate-600 border border-slate-200"
          data-testid="atlas-card-preview-label">Preview image</span>
      )}
    </>
  );
};

const VehicleCard = ({ v, selected, onToggle, disabledSelect }) => {
  const s = v.sourceData || {};
  const n = v.normalizedData || {};
  const bb = basisBadge(s.sourceCountryBasis);
  const cb = compBadge(v.comparableData);
  const photo = (s.photos || []).find((p) => p.originalUrl)?.originalUrl;
  const canToggle = selected || !disabledSelect;
  return (
    <div
      data-testid={`atlas-vehicle-card-${s.sourceCode}`}
      className={`group relative bg-card border rounded-md overflow-hidden transition-colors ${selected ? "border-atlas-blue ring-2 ring-atlas-blue/30" : "border-border hover:border-atlas-blue"}`}
    >
      <button
        type="button"
        onClick={() => canToggle && onToggle(v)}
        disabled={!canToggle}
        data-testid={`atlas-compare-toggle-${s.sourceCode}`}
        title={selected ? "Remove from comparison" : disabledSelect ? "Comparison is full (5)" : "Add to comparison"}
        className={`absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] font-medium border transition-colors
          ${selected ? "bg-atlas-blue text-white border-atlas-blue" : "bg-white/90 text-slate-700 border-slate-300 hover:border-atlas-blue"} ${!canToggle ? "opacity-40 cursor-not-allowed" : ""}`}
      >
        {selected ? <><Check className="h-3 w-3" /> Selected</> : <><GitCompare className="h-3 w-3" /> Compare</>}
      </button>
      <Link to={`/atlas/vehicle/${s.sourceCode}/${v.listingId}`} className="block">
      <div className="relative h-40 bg-muted overflow-hidden">
        <SourcePhoto src={photo} alt={`${n.brandDisplay || s.brand || ""} ${s.model || ""}`.trim()} className="w-full h-full object-cover" preview={s.urlType === "intermediary"} />
        <span className="absolute top-2 left-2 font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm bg-black/70 text-white"
          data-testid="atlas-card-source">
          {siteLabelOf(v)}
        </span>
      </div>
      <div className="p-4">
        <div className="font-display text-lg font-semibold group-hover:text-atlas-blue transition-colors">
          {n.brandDisplay || s.brand} {s.model}
        </div>
        <div className="text-sm text-muted-foreground">{s.trim || ""} {s.year ? `· ${s.year}` : ""}</div>
        {n.modelFamily && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-indigo-50 border border-indigo-200 text-indigo-700 text-[11px]"
            data-testid={`atlas-family-badge-${s.sourceCode}`}>
            <Layers className="h-3 w-3" /> <span>Family: {n.brandDisplay || s.brand} {n.modelFamily}</span>
          </div>
        )}
        <div className="mt-2" data-testid="atlas-card-price">
          <PriceTag originalPrice={s.originalPrice} originalCurrency={s.originalCurrency} size="md" testid="atlas-card-pricetag" priceIsOriginal={s.priceBasis !== "provider_eur"} />
        </div>
        <div className="text-xs text-muted-foreground mt-1">{n.mileageKm != null ? km(n.mileageKm) : "Mileage unavailable"}</div>
        <div className="mt-2">
          <AvailabilityBadge state={v.availability?.state} checkedAt={v.availability?.checkedAt} testid={`atlas-card-availability-${s.sourceCode}`} />
        </div>
        <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" /> {s.sourceCountry || "Location unknown"}
          <span className={`ml-1 px-1.5 py-0.5 rounded-sm border text-[10px] ${bb.cls}`}>{bb.label}</span>
        </div>
        <div className="mt-2 flex items-center gap-1.5">
          <BarChart3 className="h-3.5 w-3.5 text-atlas-blue" />
          <span className={`px-1.5 py-0.5 rounded-sm border text-[10px] ${cb.cls}`}>{cb.label}</span>
        </div>
      </div>
      </Link>
    </div>
  );
};

export default function AtlasVehicles() {
  const [sp] = useSearchParams();
  const hasParams = sp.toString();
  const restored = (() => {
    if (hasParams) return null;
    try { return JSON.parse(sessionStorage.getItem("atlas_last_search") || "null"); } catch { return null; }
  })();
  const [form, setForm] = useState(() => restored?.form || ({
    source: sp.get("source") || "all",
    make: sp.get("make") || "",
    model: sp.get("model") || "",
    generation: "all",
    variant: "all",
    yearMin: sp.get("year_min") || "",
    yearMax: sp.get("year_max") || "",
    priceMin: sp.get("price_min") || "",
    priceMax: sp.get("price_max") || "",
  }));
  const [data, setData] = useState(() => restored?.data || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  // Multi-vehicle comparison selection (3–5 practical set) — persisted so it
  // survives navigating to Compare and back, keyed by source:listingId identity.
  const MAX_COMPARE = 5;
  const [selected, setSelected] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("atlas_compare_set") || "[]"); } catch { return []; }
  });
  const idOf = (v) => `${v.sourceData?.sourceCode}:${v.listingId}`;
  const isSelected = (v) => selected.some((x) => idOf(x) === idOf(v));
  const toggleCompare = (v) =>
    setSelected((cur) =>
      cur.some((x) => idOf(x) === idOf(v))
        ? cur.filter((x) => idOf(x) !== idOf(v))
        : cur.length >= MAX_COMPARE ? cur : [...cur, v]);
  const clearCompare = () => setSelected([]);
  const goCompare = () => navigate("/atlas/compare");

  // Persist selection so Compare (and back-navigation) always see the same set.
  useEffect(() => {
    try { sessionStorage.setItem("atlas_compare_set", JSON.stringify(selected)); } catch { /* ignore */ }
  }, [selected]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Cascading selects: changing an upper field resets everything below it.
  const onMake = (e) => setForm((f) => ({ ...f, make: e.target.value, model: "", generation: "all", variant: "all", yearMin: "", yearMax: "" }));
  const onModel = (e) => setForm((f) => ({ ...f, model: e.target.value, generation: "all", variant: "all", yearMin: "", yearMax: "" }));
  const onGeneration = (e) => {
    const code = e.target.value;
    const gen = generationsFor(form.make, form.model).find((g) => g.code === code);
    setForm((f) => ({
      ...f, generation: code, variant: "all",
      yearMin: gen ? String(gen.yearMin) : "",
      yearMax: gen && gen.yearMax ? String(gen.yearMax) : "",
    }));
  };
  const onVariant = (e) => setForm((f) => ({ ...f, variant: e.target.value }));

  const models = modelsForMake(form.make);
  const generations = generationsFor(form.make, form.model);
  const variants = variantsFor(form.make, form.model, form.generation);

  // Monotonic request id: only the LATEST search may update visible state, so a
  // slow earlier response (Search A) can never overwrite a newer one (Search B).
  const { market, setMarket, markets, auto } = useMarket();
  const reqRef = useRef(0);
  const run = useCallback((e) => {
    if (e) e.preventDefault();
    const myReq = ++reqRef.current;
    setLoading(true); setError(false); setData(null);
    // Variant is a client-side refine on genuine returned data (Carapis model
    // filtering by sales-code is unreliable across sources) — it is NOT sent
    // upstream, so the live Carapis integration is unchanged.
    const p = {
      source: form.source,
      make: form.make || undefined,
      model: form.model || undefined,
      year_min: form.yearMin || undefined,
      year_max: form.yearMax || undefined,
      price_min: form.priceMin || undefined,
      price_max: form.priceMax || undefined,
      market: market.code,  // ISO-2 market/country; "EU" = all Europe (no filter)
      limit: 24,
    };
    searchAtlas(p)
      .then((d) => {
        if (myReq === reqRef.current) {
          setData(d);
          try { sessionStorage.setItem("atlas_last_search", JSON.stringify({ form, data: d })); } catch { /* ignore */ }
        }
      })
      .catch(() => { if (myReq === reqRef.current) setError(true); })
      .finally(() => { if (myReq === reqRef.current) setLoading(false); });
  }, [form, market.code]);

  // Auto-run when arriving from the homepage search (URL carries query params).
  const didAuto = useRef(false);
  useEffect(() => {
    if (!didAuto.current && sp.toString()) {
      didAuto.current = true;
      run();
    }
  }, [sp, run]);

  // Changing the Market re-runs the SAME search against the new market.
  const marketRef = useRef(market.code);
  useEffect(() => {
    if (marketRef.current !== market.code) {
      marketRef.current = market.code;
      if (reqRef.current > 0) run();
    }
  }, [market.code, run]);

  const allVehicles = data?.vehicles || [];
  const variantSel = form.variant && form.variant !== "all" ? form.variant : null;
  const normalize = (s) => (s || "").toLowerCase().replace(/\s+/g, "");
  const vehicles = variantSel
    ? allVehicles.filter((v) => {
        const hay = normalize(`${v.sourceData?.model || ""} ${v.sourceData?.trim || ""}`);
        return hay.includes(normalize(variantSel));
      })
    : allVehicles;
  const status = data?.status;
  const demo = data?.mode === "demo_fixtures";

  // Hide listings whose last-known status is "No longer available".
  const [hideUnavailable, setHideUnavailable] = useState(() => {
    try { return localStorage.getItem("atlas_hide_unavail") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("atlas_hide_unavail", hideUnavailable ? "1" : "0"); } catch { /* ignore */ }
  }, [hideUnavailable]);
  const unavailCount = vehicles.filter((v) => v.availability?.state === "UNAVAILABLE").length;
  const visibleVehicles = hideUnavailable
    ? vehicles.filter((v) => v.availability?.state !== "UNAVAILABLE")
    : vehicles;

  // Client-side sort over the ALREADY-returned results (never triggers Carapis).
  const [sortBy, setSortBy] = useState("relevance");
  const num = (x) => (typeof x === "number" && !isNaN(x) ? x : null);
  const ts = (v) => {
    const t = v.availability?.checkedAt || v.sourceData?.lastSeenAt;
    const d = t ? new Date(t).getTime() : NaN;
    return isNaN(d) ? null : d;
  };
  const cmpNullsLast = (a, b, dir) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return dir === "asc" ? a - b : b - a;
  };
  const sortedVehicles = (() => {
    const arr = [...visibleVehicles];
    if (sortBy === "price_asc") arr.sort((a, b) => cmpNullsLast(num(a.normalizedData?.normalizedPrice), num(b.normalizedData?.normalizedPrice), "asc"));
    else if (sortBy === "price_desc") arr.sort((a, b) => cmpNullsLast(num(a.normalizedData?.normalizedPrice), num(b.normalizedData?.normalizedPrice), "desc"));
    else if (sortBy === "mileage_asc") arr.sort((a, b) => cmpNullsLast(num(a.normalizedData?.mileageKm), num(b.normalizedData?.mileageKm), "asc"));
    else if (sortBy === "newest") arr.sort((a, b) => cmpNullsLast(ts(a), ts(b), "desc") || cmpNullsLast(num(a.sourceData?.year), num(b.sourceData?.year), "desc"));
    return arr;
  })();

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-10" data-testid="atlas-vehicles-page">
      <div className="flex items-center gap-2 text-atlas-blue mb-2">
        <Database className="h-5 w-5" />
        <span className="overline">Atlas live search · Auto API (AutoScout24 · Mobile.de) + TheCarAPI (Otomoto · Marktplaats · Leboncoin · 30+ EU markets)</span>
      </div>
      <h1 className="font-display text-4xl font-bold tracking-tight">Search the European market</h1>
      <p className="text-muted-foreground mt-3 max-w-2xl">
        Real listings retrieved live and normalized into Atlas. Original price and currency are
        preserved; the EUR figure is a comparison value. Atlas links to the original listing.
      </p>

      {/* Search form — cascading Make → Model → Generation → Variant */}
      <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="atlas-market-bar">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Market:</span>
          <select
            value={market.code}
            onChange={(e) => setMarket(e.target.value)}
            data-testid="atlas-market-select"
            className="h-9 rounded-md border border-border bg-background px-3 text-sm font-medium">
            {markets.map((m) => (
              <option key={m.code} value={m.code}>
                {m.label}{typeof m.count === "number" ? ` (${m.count})` : ""}
              </option>
            ))}
          </select>
        </div>
        {auto ? (
          <span className="text-xs text-muted-foreground" data-testid="atlas-market-hint">Based on your location · you can change it anytime</span>
        ) : (
          <span className="text-xs text-muted-foreground">Searching the {market.label} market</span>
        )}
      </div>

      <form onSubmit={run} className="mt-3 bg-card border border-border rounded-md p-4 grid gap-3 md:grid-cols-4"
        data-testid="atlas-search-form">
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground mb-1">Source</span>
          <select value={form.source} onChange={set("source")} data-testid="atlas-search-source"
            className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm">
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground mb-1">Make</span>
          <select value={form.make} onChange={onMake} data-testid="atlas-search-make"
            className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm">
            <option value="">Select make…</option>
            {catalogMakes().map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground mb-1">Model</span>
          <select value={form.model} onChange={onModel} data-testid="atlas-search-model"
            disabled={!form.make}
            className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm disabled:opacity-50">
            <option value="">All models</option>
            {models.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground mb-1">Generation</span>
          <select value={form.generation} onChange={onGeneration} data-testid="atlas-search-generation"
            disabled={!form.model}
            className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm disabled:opacity-50">
            <option value="all">All generations</option>
            {generations.map((g) => <option key={g.code} value={g.code}>{g.code} — {g.years}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground mb-1">Variant / trim</span>
          <select value={form.variant} onChange={onVariant} data-testid="atlas-search-variant"
            disabled={!variants.length}
            className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm disabled:opacity-50">
            <option value="all">All variants</option>
            {variants.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2 md:col-span-2">
          <label className="text-sm"><span className="block text-xs text-muted-foreground mb-1">Price min (EUR)</span>
            <Input value={form.priceMin} onChange={set("priceMin")} inputMode="numeric" placeholder="5000" data-testid="atlas-search-pricemin" /></label>
          <label className="text-sm"><span className="block text-xs text-muted-foreground mb-1">Price max (EUR)</span>
            <Input value={form.priceMax} onChange={set("priceMax")} inputMode="numeric" placeholder="40000" data-testid="atlas-search-pricemax" /></label>
        </div>
        <div className="flex items-end">
          <Button type="submit" disabled={loading} data-testid="atlas-search-submit"
            className="w-full bg-atlas-ink hover:bg-atlas-blue text-white">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Searching…</> : <><SearchIcon className="h-4 w-4 mr-2" /> Search live</>}
          </Button>
        </div>
        {form.generation !== "all" && (
          <p className="text-[11px] text-muted-foreground md:col-span-4 -mt-1">
            Generation filters live results by production years ({form.yearMin || "…"}–{form.yearMax || "present"}). Variant refines the returned listings.
          </p>
        )}
      </form>

      {/* States */}
      {loading && (
        <div className="mt-10 text-center text-muted-foreground" data-testid="atlas-search-loading">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" /> <p className="mt-2 text-sm">Retrieving live listings…</p>
        </div>
      )}

      {!loading && error && (
        <div className="mt-10 text-center" data-testid="atlas-search-neterror">
          <AlertCircle className="h-8 w-8 text-amber-500 mx-auto" />
          <p className="mt-2 text-sm text-muted-foreground">Couldn't reach Atlas. Please try again.</p>
        </div>
      )}

      {!loading && data && (
        <div className="mt-8">
          {demo && (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm px-4 py-2.5" data-testid="atlas-demo-banner">
              DEMO / TEST data — Carapis is not configured. This is not live market data.
            </div>
          )}
          {status === "OK" && (
            <>
              {data.partial && (
                <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm px-4 py-2.5" data-testid="atlas-partial-banner">
                  Partial results — some sources timed out or were rate-limited. Showing what we could retrieve.
                </div>
              )}
              {Array.isArray(data.notices) && data.notices.length > 0 && (
                <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 text-slate-600 text-xs px-4 py-2" data-testid="atlas-provider-notice">
                  {data.notices.join(" ")}
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-muted-foreground" data-testid="atlas-results-meta">
                  Showing <span className="text-foreground font-medium">{visibleVehicles.length}</span> live listing{visibleVehicles.length === 1 ? "" : "s"}
                  {variantSel
                    ? <> matching <span className="text-foreground font-medium">“{variantSel}”</span> ({allVehicles.length} retrieved)</>
                    : (typeof data.count === "number" && <> · {data.count} matched across sources</>)}
                </p>
                {!demo && (data.cached ? (
                  <span className="text-xs text-slate-600 bg-slate-100 border border-slate-200 rounded-sm px-2 py-0.5" data-testid="atlas-cached-badge">
                    Cached · refreshes every {Math.max(1, Math.round((data.cacheTtlSeconds || 300) / 60))} min
                  </span>
                ) : (
                  <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-0.5" data-testid="atlas-live-badge">{data.mode === "auto_api" ? "Live Auto API data" : "Live Carapis data"}</span>
                ))}
              </div>

              {/* Availability filter + freshness legend */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-5" data-testid="atlas-results-controls">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <Switch checked={hideUnavailable} onCheckedChange={setHideUnavailable} data-testid="atlas-hide-unavailable-toggle" />
                  <span>Hide “No longer available”{unavailCount > 0 ? <span className="text-muted-foreground"> ({unavailCount})</span> : ""}</span>
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="atlas-freshness-legend-trigger"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-atlas-blue">
                      <Info className="h-3.5 w-3.5" /> What does “Last known” mean?
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-80 text-xs leading-relaxed" data-testid="atlas-freshness-legend">
                    <p className="font-medium text-sm mb-2">Availability is a last-known status</p>
                    <ul className="space-y-1.5">
                      <li className="flex gap-2"><span className="mt-1 h-2 w-2 rounded-full bg-emerald-500 shrink-0" /><span><b>Live</b> — the source confirmed this listing within the last 48 hours.</span></li>
                      <li className="flex gap-2"><span className="mt-1 h-2 w-2 rounded-full bg-slate-400 shrink-0" /><span><b>Last known: listed</b> — the source listed it on the shown date but hasn’t re-confirmed it recently. It may or may not still be available — check the original listing.</span></li>
                      <li className="flex gap-2"><span className="mt-1 h-2 w-2 rounded-full bg-red-500 shrink-0" /><span><b>No longer available</b> — the source reports the listing has been removed.</span></li>
                      <li className="flex gap-2"><span className="mt-1 h-2 w-2 rounded-full bg-amber-500 shrink-0" /><span><b>Availability not verified</b> — no availability signal from the source.</span></li>
                    </ul>
                    <p className="mt-2 text-muted-foreground">Timestamps come from the data provider. Marketplaces restrict automated live re-checks, so Atlas shows the last-known status rather than guessing.</p>
                  </PopoverContent>
                </Popover>
                <label className="flex items-center gap-2 text-xs text-muted-foreground ml-auto">
                  <span className="hidden sm:inline">Sort</span>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="atlas-sort-select"
                    className="h-8 rounded-sm border border-border bg-background px-2 text-xs text-foreground">
                    <option value="relevance">Relevance</option>
                    <option value="price_asc">Price: low to high</option>
                    <option value="price_desc">Price: high to low</option>
                    <option value="mileage_asc">Mileage: low to high</option>
                    <option value="newest">Newest / most recently verified</option>
                  </select>
                </label>
              </div>

              {visibleVehicles.length === 0 ? (
                <div className="rounded-md border border-border bg-muted/40 px-4 py-8 text-center text-sm text-muted-foreground" data-testid="atlas-variant-empty">
                  {variantSel
                    ? <>None of the {allVehicles.length} retrieved listing{allVehicles.length === 1 ? "" : "s"} match variant “{variantSel}”. Try <button type="button" onClick={() => setForm((f) => ({ ...f, variant: "all" }))} className="text-atlas-blue underline">All variants</button> or a broader search.</>
                    : hideUnavailable && unavailCount > 0
                      ? <>All {unavailCount} retrieved listing{unavailCount === 1 ? " is" : "s are"} marked “No longer available”. <button type="button" onClick={() => setHideUnavailable(false)} className="text-atlas-blue underline">Show them</button>.</>
                      : <>No live listings matched your search.</>}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="atlas-vehicles-grid">
                  {sortedVehicles.map((v) => (
                    <VehicleCard
                      key={`${v.sourceData.sourceCode}:${v.listingId}`}
                      v={v}
                      selected={isSelected(v)}
                      onToggle={toggleCompare}
                      disabledSelect={selected.length >= MAX_COMPARE}
                    />
                  ))}
                </div>
              )}
            </>
          )}
          {status === "EMPTY" && (
            <div className="text-center py-12" data-testid="atlas-empty">
              <SearchIcon className="h-8 w-8 text-muted-foreground mx-auto" />
              <p className="mt-3 font-medium">No live listings matched your search.</p>
              <p className="text-sm text-muted-foreground">Try a broader brand/model or a different source.</p>
            </div>
          )}
          {["AUTH_FAILED", "RATE_LIMITED", "TIMEOUT", "MALFORMED", "ERROR", "UNSUPPORTED_SOURCE", "NOT_CONFIGURED"].includes(status) && !demo && (
            <div className="text-center py-12" data-testid="atlas-status-error">
              <AlertCircle className="h-8 w-8 text-amber-500 mx-auto" />
              <p className="mt-3 font-medium">{status.replace(/_/g, " ")}</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">{data.message}</p>
            </div>
          )}
        </div>
      )}

      {!loading && !data && !error && (
        <p className="mt-10 text-sm text-muted-foreground" data-testid="atlas-idle">
          Enter your criteria and search across Auto API (AutoScout24, Mobile.de) and TheCarAPI’s broad European classifieds (Otomoto, Marktplaats, Leboncoin and more).
        </p>
      )}

      {/* Comparison selection bar */}
      {selected.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-50 border-t border-border bg-white/95 backdrop-blur-xl shadow-[0_-4px_20px_rgba(0,0,0,0.06)]"
          data-testid="atlas-compare-bar">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm min-w-0">
              <GitCompare className="h-4 w-4 text-atlas-blue shrink-0" />
              <span className="font-medium shrink-0" data-testid="atlas-compare-count">{selected.length} selected</span>
              <span className="text-muted-foreground truncate hidden sm:inline">
                {selected.length < 2 ? "Select at least one more to compare" : `Compare up to ${MAX_COMPARE} vehicles`}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="ghost" onClick={clearCompare} data-testid="atlas-compare-clear">
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
              <Button
                className="bg-atlas-ink hover:bg-atlas-blue text-white disabled:opacity-50"
                disabled={selected.length < 2}
                onClick={goCompare}
                data-testid="atlas-compare-go"
              >
                <GitCompare className="h-4 w-4 mr-2" /> Compare {selected.length}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
